import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Phone, ArrowRight, Zap, ShieldCheck } from 'lucide-react';
import { trackBlogCtaClick } from '../../utils/analytics';
import { APP_CONFIG } from '../../config/app';

interface ArticleCtaProps {
  variant?: 'inline' | 'sticky';
  articleSlug?: string;
}

const WHATSAPP_URL = APP_CONFIG.contact.whatsappUrl;
const PHONE_URL = `tel:${APP_CONFIG.contact.phoneTel}`;

const ArticleCta: React.FC<ArticleCtaProps> = ({ variant = 'inline', articleSlug }) => {
  const scrollToContact = () => {
    trackBlogCtaClick('Formulaire', articleSlug);
    const el = document.getElementById('contact');
    if (el) {
      const offset = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    } else {
      window.location.href = '/depannage-informatique#contact';
    }
  };

  if (variant === 'sticky') {
    return (
      <motion.div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.8, ease: 'easeOut' }}
      >
        <div className="rounded-2xl border border-[#5865F2]/40 bg-[#2F3136]/95 backdrop-blur-md p-4 shadow-2xl flex items-center justify-between gap-2">
          <p className="text-white text-sm font-medium leading-tight">
            Problème identifié ? On le résout <span className="text-[#B9C4FF]">aujourd'hui</span>.
          </p>
          <div className="flex-shrink-0 flex items-center gap-2">
            <button
              type="button"
              onClick={scrollToContact}
              className="inline-flex items-center gap-1 px-2.5 py-2 bg-white/5 border border-white/15 text-white text-xs font-medium rounded-lg hover:bg-white/10 transition-colors"
            >
              Formulaire
            </button>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBlogCtaClick('WhatsApp', articleSlug)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#5865F2] to-[#4752C4] text-white text-sm font-medium rounded-lg"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="rounded-2xl border border-[#5865F2]/35 bg-gradient-to-r from-[#5865F2]/20 via-[#5865F2]/10 to-cyan-400/10 p-6 md:p-8"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div className="flex flex-wrap gap-3 mb-4">
        <span className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-gray-200">
          <Zap className="h-3.5 w-3.5 text-[#5865F2]" /> Dépannage à distance rapide
        </span>
        <span className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-gray-200">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Diagnostic gratuit
        </span>
      </div>

      <h3 className="text-lg md:text-xl font-bold text-white mb-2">
        Votre problème n'est pas résolu ?
      </h3>
      <p className="text-gray-300 text-sm mb-6 leading-relaxed">
        Un technicien prend en main votre PC à distance et règle le problème sous 48h.
        Vous payez uniquement si c'est résolu.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <motion.a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackBlogCtaClick('WhatsApp — Réponse rapide', articleSlug)}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300"
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <MessageCircle className="h-4 w-4" />
          WhatsApp — Réponse rapide
        </motion.a>

        <motion.a
          href={PHONE_URL}
          onClick={() => trackBlogCtaClick('Appeler', articleSlug)}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white/5 border border-white/15 text-white font-medium rounded-lg hover:bg-white/10 transition-all duration-300"
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <Phone className="h-4 w-4" />
          Appeler
        </motion.a>

        <motion.button
          type="button"
          onClick={scrollToContact}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white/5 border border-white/15 text-white font-medium rounded-lg hover:bg-white/10 transition-all duration-300"
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          Formulaire <ArrowRight className="h-4 w-4" />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ArticleCta;
